import { useEffect } from 'react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useAuth } from './auth'
import Layout from './components/Layout'
import RequireAdmin from './components/RequireAdmin'
import StudioLayout from './components/StudioLayout'
import Login from './pages/Login'
import Workspace from './pages/Workspace'
import Settings from './pages/Settings'
// Admin surfaces — only reachable under /studio/* (or /artifact/:id deep link)
import PipelinePage from './pages/PipelinePage'
import PromptsPage from './pages/PromptsPage'
import Observability from './pages/Observability'
import StudioIndex from './pages/studio/Index'
import TestSets from './pages/studio/TestSets'
import PipelineTest from './pages/studio/PipelineTest'
import AIConfig from './pages/studio/AIConfig'
import ArtifactViewer from './pages/ArtifactViewer'
import Artifacts from './pages/Artifacts'
import ImportV1 from './pages/ImportV1'

export default function App() {
  const auth = useAuth()
  // One-time cleanup of legacy `me_chat_history::*` localStorage keys —
  // pre-task-X.A the chat panel persisted message arrays per scope into
  // localStorage. We're fully server-side now (apps/chat/models.py +
  // /api/chat/history/), but old keys linger on returning users'
  // browsers. They're not read anywhere current; just wipe them so
  // they don't show up in DevTools and confuse anyone (including us)
  // about whether chat is genuinely browser-stored.
  useEffect(() => {
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i)
        if (k && k.startsWith('me_chat_history::')) localStorage.removeItem(k)
      }
    } catch { /* ignore — Safari private mode etc. */ }
  }, [])
  // Track the most recent non-artifact-viewer SPA location so the
  // ArtifactViewer's back button can return to the exact list view
  // (including ?filter=, ?q=, ?sort= etc.) the user was on. document.referrer
  // only reflects full page loads, not in-app navigation, so it's
  // unreliable on its own — clicking from /studio/artifacts after having
  // loaded the tab on /studio/observability would still report observability.
  const location = useLocation()
  useEffect(() => {
    const path = location.pathname + location.search
    // Skip artifact-viewer paths (so the viewer's back button doesn't loop
    // to itself) AND /login (a transitional gateway, never a real "prior
    // page" — caused contamination after auth-expiry redirect chains
    // landing back on /workspace via the wildcard).
    if (path.startsWith('/artifact/')
        || path.startsWith('/studio/artifact/')
        || path === '/login') return
    try { sessionStorage.setItem('me_last_browser_location', path) } catch { /* ignore */ }
  }, [location])
  // When the user becomes unauthenticated (manual logout, expired
  // session, fresh tab), wipe the back-button breadcrumb so the next
  // user (or same user logging back in) doesn't inherit the previous
  // session's last location. SessionStorage is per-tab, so this only
  // affects this tab — other open tabs keep their own state.
  useEffect(() => {
    if (auth.status === 'unauthenticated') {
      try { sessionStorage.removeItem('me_last_browser_location') } catch { /* ignore */ }
    }
  }, [auth.status])
  if (auth.status === 'loading') {
    // Circular spinner — same pattern used in Artifacts / Workspace /
    // MatterTimelinePane for consistency. Shown on every cold load
    // (including /login, where it bridges the gap between blank page
    // and Login form fading in).
    return (
      <div className="grid h-full place-items-center">
        <div className="size-8 rounded-full border-[3px] border-[color:var(--me-textMute)] border-t-transparent animate-spin" />
      </div>
    )
  }
  if (auth.status === 'unauthenticated') {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    )
  }
  return (
    <>
      <Routes>
        <Route element={<Layout />}>
          <Route path="/" element={<Navigate to="/workspace" replace />} />
          <Route path="/workspace" element={<Workspace />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/artifact/:id" element={<ArtifactViewer />} />
          {/* Old top-level admin URLs — preserved as redirects so existing
              bookmarks / docs / external links don't 404 after the
              consolidation under /studio. */}
          <Route path="/artifacts" element={<Navigate to="/studio/artifacts" replace />} />
          <Route path="/pipeline" element={<Navigate to="/studio/pipeline" replace />} />
          <Route path="/prompts" element={<Navigate to="/studio/prompts" replace />} />
          <Route path="/observability" element={<Navigate to="/studio/observability" replace />} />
          <Route path="/imports/v1" element={<Navigate to="/studio/imports" replace />} />
        </Route>
        <Route path="/studio" element={<RequireAdmin><StudioLayout /></RequireAdmin>}>
          <Route index element={<StudioIndex />} />
          <Route path="artifacts" element={<Artifacts />} />
          {/* Studio-flavored artifact viewer: same component, different
              parent layout (StudioLayout's nav). Linked from /studio/artifacts
              and other Studio-internal flows. End users without Studio
              access can't reach this URL — RequireAdmin gates the entire
              /studio/* tree, so the workspace remains the only artifact
              viewer for them. */}
          <Route path="artifact/:id" element={<ArtifactViewer />} />
          <Route path="pipeline" element={<PipelinePage />} />
          <Route path="pipeline-test" element={<PipelineTest />} />
          <Route path="prompts" element={<PromptsPage />} />
          <Route path="test-sets" element={<TestSets />} />
          <Route path="observability" element={<Observability />} />
          <Route path="imports" element={<ImportV1 />} />
          <Route path="ai-config" element={<AIConfig />} />
        </Route>
        <Route path="*" element={<Navigate to="/workspace" replace />} />
      </Routes>
    </>
  )
}
